<template>
    <section ref="sectionRef" class="py-16 lg:py-[150px] px-5 lg:px-[120px]" :style="{ backgroundColor }">
        <div class="mx-auto">
            <!-- Headline -->
            <h2 class="text-3xl md:text-4xl lg:text-[44px] lg:leading-[57px] font-semibold text-center mb-[50px]" :style="{ color: titleColor }">
                <span v-html="renderTitle()"></span>
            </h2>

            <!-- Stepper / Timeline -->
            <div class="timeline-wrapper mx-auto mt-10 mb-16">
                <!-- ─── Mobile: vertikale Track + Fill ─── -->
                <div class="timeline-line-vertical-track" :style="{ backgroundColor: lineColor }">
                    <div class="timeline-line-vertical-fill" :style="{ height: `${progress * 100}%`, backgroundColor: activeStepColor }" />
                </div>

                <!-- ─── Desktop: horizontale Track + Fill ─── -->
                <div class="timeline-line-horizontal-track" :style="{ left: lineLeftPosition, right: lineRightPosition, backgroundColor: lineColor }">
                    <div class="timeline-line-horizontal-fill" :style="{ width: `${progress * 100}%`, backgroundColor: activeStepColor }" />
                </div>

                <!-- Steps -->
                <div v-for="(step, index) in steps" :key="index" class="timeline-step">
                    <!-- Icon Circle – Farbe wechselt wenn der Fortschritt den Schritt erreicht -->
                    <div
                        class="timeline-icon w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-500"
                        :style="{ backgroundColor: isStepActive(index) ? activeStepColor : inactiveStepColor }"
                    >
                        <img v-if="step.iconImage" :src="$asset(step.iconImage, 'public')" class="w-8 h-8" :alt="step.label" />
                        <svg v-else class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path v-for="(path, pIndex) in step.icon.paths" :key="pIndex" v-bind="path" />
                            <rect v-for="(rect, rIndex) in step.icon.rects" :key="`r-${rIndex}`" v-bind="rect" />
                            <circle v-for="(circle, cIndex) in step.icon.circles" :key="`c-${cIndex}`" v-bind="circle" />
                        </svg>
                    </div>

                    <!-- Label -->
                    <p class="timeline-label text-[20px] font-semibold text-center px-2 mt-3" :style="{ color: labelColor }">
                        {{ step.label }}
                    </p>
                </div>
            </div>

            <!-- Disclaimer -->
            <p v-if="disclaimer" class="text-[18px] text-center mt-10 w-full mx-auto font-pro" :style="{ color: disclaimerColor }">
                {{ disclaimer }}
            </p>
        </div>
    </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    backgroundColor: { type: String, default: '#ffffff' },
    title: { type: String, required: true },
    titleHighlight: { type: String, default: '' },
    titleHighlightColor: { type: String, default: '#0d9488' },
    titleHighlightGradientFrom: { type: String, default: '' },
    titleHighlightGradientTo: { type: String, default: '' },
    titleColor: { type: String, default: '#1a1a1a' },
    steps: { type: Array, required: true },
    disclaimer: { type: String, default: '' },
    disclaimerColor: { type: String, default: '#4b5563' },
    activeStepColor: { type: String, default: '#2563eb' },
    inactiveStepColor: { type: String, default: '#1e3a5f' },
    lineColor: { type: String, default: '#1e3a5f' },
    labelColor: { type: String, default: '#1a1a1a' }
});

// ─── Animation state ───────────────────────────────────────────────
const sectionRef = ref(null);
const progress = ref(0); // 0 → 1

// Scroll-Handler: progress = wie weit die Section durch den Viewport gescrollt wurde
const onScroll = () => {
    const el = sectionRef.value;

    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;

    // Start: Section-Oberkante erreicht Viewport-Unterkante → progress 0
    // Ende:  Section-Unterkante verlässt Viewport-Oberkante → progress 1
    const total = rect.height + vh * 0.4;
    const done = vh - rect.top;

    progress.value = Math.min(Math.max(done / total, 0), 1) + 0;
};

onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Initialwert setzen (falls Section schon sichtbar)
});

onUnmounted(() => {
    window.removeEventListener('scroll', onScroll);
});

// ─── Schritt aktiv? Schwelle = i / (n-1) ──────────────────────────
const isStepActive = (index) => {
    const n = props.steps.length;

    if (n <= 1) return true;

    const threshold = index / (n - 1);

    return progress.value >= threshold;
};

// ─── Line-Position (unveränderlich) ───────────────────────────────
const lineLeftPosition = computed(() => `calc(100% / ${props.steps.length * 2})`);
const lineRightPosition = computed(() => `calc(100% / ${props.steps.length * 2})`);

// ─── Title mit Highlight ──────────────────────────────────────────
const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    const highlightStyle =
        props.titleHighlightGradientFrom && props.titleHighlightGradientTo
            ? `background: linear-gradient(to top, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;`
            : `color: ${props.titleHighlightColor};`;

    return parts.map((part, index) => (index === 0 ? part : `<span style="${highlightStyle}">${props.titleHighlight}</span>${part}`)).join('');
};
</script>

<style scoped>
/* ── Mobile: Vertikale Timeline ── */
.timeline-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding-left: 2rem;
}

/* Vertikaler Track (Hintergrund) */
.timeline-line-vertical-track {
    position: absolute;
    left: 24px;
    top: 24px;
    bottom: 24px;
    width: 5px;
    border-radius: 9999px;
    overflow: hidden;
    z-index: 0;
}

/* Vertikaler Fill (wächst von oben) */
.timeline-line-vertical-fill {
    width: 100%;
    border-radius: 9999px;
}

/* Horizontale Linie auf Mobile verstecken */
.timeline-line-horizontal-track {
    display: none;
}

.timeline-step {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 1rem;
}

.timeline-icon {
    position: relative;
    margin-left: -2rem;
}

.timeline-label {
    flex: 1;
    text-align: left;
    margin-top: 0.75rem;
    white-space: normal;
}

/* ── Desktop: Horizontale Timeline ── */
@media (min-width: 1024px) {
    .timeline-wrapper {
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0;
        padding-left: 0;
    }

    /* Vertikale Linie auf Desktop verstecken */
    .timeline-line-vertical-track {
        display: none;
    }

    /* Horizontaler Track (Hintergrund-Balken) */
    .timeline-line-horizontal-track {
        display: block;
        position: absolute;
        top: 24px;
        height: 5px;
        border-radius: 9999px;
        overflow: hidden;
        z-index: 0;
    }

    /* Horizontaler Fill (wächst von links) */
    .timeline-line-horizontal-fill {
        height: 100%;
        border-radius: 9999px;
    }

    .timeline-step {
        flex: 1;
        flex-direction: column;
        align-items: center;
        gap: 0;
    }

    .timeline-icon {
        margin-left: 0;
    }

    .timeline-label {
        text-align: center;
        margin-top: 0.75rem;
        white-space: pre-line;
    }
}
</style>
